import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Slider,
  TextField,
  CircularProgress,
  Divider,
  Paper,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getBullionRatesRequest } from "../../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../../theme/useAppTheme";
import useCurrency from "../../../hooks/useCurrency";

const format = (v) => Number(v || 0).toFixed(2);

export default function ProductPriceCalculator() {
  const dispatch = useDispatch();
  const { bullionRates, isLoading } = useSelector((s) => s.dashboard || {});

  const [selectedKey, setSelectedKey] = useState("");
  const [selectedRate, setSelectedRate] = useState(0);
  const [makingPercentage, setMakingPercentage] = useState(10);
  const [grams, setGrams] = useState("");

  const themeColors = useAppTheme();
  const { symbol } = useCurrency();

  useEffect(() => {
    dispatch(
      getBullionRatesRequest({
        id: process.env.REACT_APP_JEWELLER_ID,
      }),
    );
  }, [dispatch]);

  const enabledBullions = useMemo(() => {
    const filtered = (bullionRates || []).filter((r) => r.rateType === "Buy");

    console.log("💛 ENABLED BULLIONS:", filtered);

    return filtered;
  }, [bullionRates]);

  const totalPrice = useMemo(() => {
    console.log("--------- TOTAL PRICE CALCULATION ---------");
    console.log("Selected Rate:", selectedRate);
    console.log("Entered Grams:", grams);
    console.log("Making %:", makingPercentage);

    if (!selectedRate || !grams) {
      console.log("❌ Missing selectedRate OR grams → Returning 0");
      return 0;
    }

    const metalPrice = selectedRate * Number(grams);
    const makingCharge = metalPrice * (makingPercentage / 100);
    const total = metalPrice + makingCharge;

    console.log("Metal Price:", metalPrice);
    console.log("Making Charge:", makingCharge);
    console.log("TOTAL PRICE:", total);

    return total;
  }, [selectedRate, grams, makingPercentage]);

  if (isLoading && !bullionRates.length) {
    return (
      <Box
        minHeight="60vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        border: `1px solid ${themeColors.scaffold}`,
        p: { xs: 2, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 3,
          boxShadow: 5,
          p: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            color: themeColors.primary,
            mb: 3,
          }}
        >
          Jewellery Price Calculator
        </Typography>

        {/* Metal Selection */}
        <Card
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" mb={1}>
            Select Metal
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <RadioGroup
            value={selectedKey}
            onChange={(e) => {
              const key = e.target.value;
              console.log("🔥 SELECTED KEY:", key);

              const bullion = enabledBullions.find((b) => {
                const k =
                  (b.bullion?.metalName || "") +
                  (b.bullion?.purity || "") +
                  (b.bullion?.karat || "");

                console.log("Checking bullion key:", k);

                return k === key;
              });

              console.log("Matched Bullion Object:", bullion);
              console.log("Matched rateWithGst:", bullion?.rateWithGst);

              setSelectedKey(key);
              setSelectedRate(bullion?.rateWithGst || 0);
            }}
          >
            {enabledBullions.map((b) => {
              const key =
                (b.bullion?.metalName || "") +
                (b.bullion?.purity || "") +
                (b.bullion?.karat || "");

              console.log("💠 Rendering bullion card with key:", key);

              return (
                <Card
                  key={key}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    color: themeColors.text,
                    background:
                      selectedKey === key
                        ? themeColors.primary
                        : themeColors.bigContainer,
                    borderRadius: 2,
                    border:
                      selectedKey === key
                        ? `1px solid ${themeColors.scaffold}`
                        : `1px solid ${themeColors.primary}`,
                  }}
                >
                  <FormControlLabel
                    value={key}
                    control={<Radio color="primary" />}
                    label={
                      <Box>
                        <Typography fontWeight={600}>
                          {b.bullion?.metalName} {b.bullion?.karat}K{" "}
                          {b.bullion?.purity}
                        </Typography>
                        <Typography fontSize={14}>
                          {symbol}{format(b.rateWithGst)}/gram
                        </Typography>
                      </Box>
                    }
                  />
                </Card>
              );
            })}
          </RadioGroup>
        </Card>

        <Card
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" mb={1}>
            Making Charges (%)
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography fontWeight={600}>
            Making Charges in Percentage(%) : {makingPercentage.toFixed(0)}
          </Typography>

          <Slider
            value={makingPercentage}
            min={0}
            max={100}
            step={1}
            onChange={(e, val) => {
              console.log("Making % Changed To:", val);
              setMakingPercentage(val);
            }}
            sx={{ color: themeColors.primary }}
          />
        </Card>

        <Card
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" mb={1}>
            Enter Grams
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            label="Enter grams"
            type="number"
            value={grams}
            onChange={(e) => {
              console.log("Grams Entered:", e.target.value);
              setGrams(e.target.value);
            }}
            inputProps={{ step: "0.01" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: themeColors.mediumContainer,
                color: themeColors.text,
              },
              "& .MuiInputLabel-root": {
                color: themeColors.text,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.scaffold,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
            }}
          />
        </Card>

        <Paper
          elevation={3}
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: 3,
            background: themeColors.primary,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          <Typography variant="h6">Total Jewellery Price</Typography>

          <Typography variant="h4" fontWeight={700} mt={1}>
            {symbol}{format(totalPrice)}
          </Typography>
        </Paper>
      </Card>
    </Box>
  );
}
